import { useState } from 'react'
import { formatDateReplaceHour } from 'src/utils/formatDate'
import * as S from './FormCountBatches.styles'
import { Typography, IconButton, InputAdornment } from '@mui/material'
import { useForm } from 'react-hook-form'
import { handleOnKeyDown } from 'src/utils/Form'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { actions } from '../../../TransfersState'
import { ReceiveCountBatch } from 'src/features/transfers/TransfersTypes'

interface FormCountBatchesProps {
  idCage: number
  idModel: number
  idLocation: number
  productDescription: string
  supervisorBadge: string
  nextStep: () => void
}

const FormCountBatches = ({
  idCage,
  idModel,
  idLocation,
  productDescription,
  supervisorBadge,
  nextStep,
}: FormCountBatchesProps) => {
  const {
    register, handleSubmit, reset, setFocus, formState: { errors },
  } = useForm<ReceiveCountBatch>()

  const dispatch = useDispatch()

  const [batches, setBatches] = useState<ReceiveCountBatch[]>([])

  const tableColumns = ['Validade', 'Quantidade', 'Remover']

  const [showModalCountConfirmation, setShowModalCountConfirmation] = useState(false)

  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  const countBatch = (batch: ReceiveCountBatch) => {
    const quantity = Number(batch.quantity)

    setBatches([...batches, {
      quantity,
      expirationDate: batch.expirationDate,
    }])

    reset()
    setFocus('quantity')
  }

  const removeBatch = (index: number) => {
    const updatedBatches = [...batches]
    updatedBatches.splice(index, 1)
    setBatches(updatedBatches)
  }

  const saveCountedBatches = () => {
    if (preventDoubleClick) {
      return
    }

    if (batches.length === 0) {
      toast.error('Conte ao menos um lote para continuar!')
      return
    }

    if (!showModalCountConfirmation) {
      setShowModalCountConfirmation(true)
      return
    }

    setPreventDoubleClick(true)

    dispatch(actions.receiveSupervisorCountBatches({
      badge: supervisorBadge,
      idCage,
      idModel,
      idLocation,
      batches,
      onSuccess: (goToNextStep: boolean) => {
        setShowModalCountConfirmation(false)
        toast.success('Contagem de lotes enviada com sucesso!')
        if (goToNextStep) {
          nextStep()
        } else {
          toast.info('Rota recalculada devido a recontagem!')
        }
      },
      setPreventDoubleClick,
    }))
  }

  return (
    <>
      <S.Container>
        <S.Form onSubmit={handleSubmit(countBatch)}>
          <S.FormInputs>
            <S.CustomInputText
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ cursor: 'pointer' }}
                    data-testid='input-adornment-test-id'
                  >
                    UN
                  </InputAdornment>
                ),
                inputProps: {
                  ...register('quantity', {
                    required: 'Campo obrigatório',
                    min: { value: 1, message: 'Deve ser um valor positivo' },
                    pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser inteiro' },
                  }),
                  'data-testid': 'input-quantity-test-id',
                },
              }}
              id='quantity'
              name='quantity'
              testId='input-quantity-test-id'
              label='Quantidade'
              type='number'
              autoComplete='off'
              onKeyDown={(e) => handleOnKeyDown(e, 'expirationDate')}
              error={!!errors.quantity}
              helperText={errors.quantity?.message}
              autoFocus
            />
            <S.CustomInputText
              inputProps={{
                ...register('expirationDate', { required: 'Campo obrigatório' }),
                'data-testid': 'input-expiration-date-test-id',
              }}
              id='expirationDate'
              name='expirationDate'
              testId='input-expiration-date-test-id'
              label='Data de validade'
              type='date'
              autoComplete='off'
              error={!!errors.expirationDate}
              helperText={errors.expirationDate?.message}
              InputLabelProps={{ shrink: true }}
            />
          </S.FormInputs>
          <S.CustomButton
            sx={{ marginTop: '.7rem !important' }}
            type='submit'
            title='Contar Lote'
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            data-testid='button-count-batch-test-id'
          />
          <Typography sx={{ fontSize: '15px' }} data-testid='total-quantity-info-test-id'>
            Total: <b>{batches.reduce((acc, batch) => acc + batch.quantity, 0)}</b>
          </Typography>
          <S.ContainerRows>
            <S.Row style={{
              position: 'sticky', top: 0, background: '#EFEFEF', zIndex: 1,
            }}
            >
              {tableColumns.map((column, index) => (
                <S.Column key={index}>
                  <Typography sx={{ fontWeight: 'bold' }}>{column}</Typography>
                </S.Column>
              ))}
            </S.Row>
            {batches.length > 0 ? batches.map((batch, index) => ({ batch, index })).reverse().map(({ batch, index }) => (
              <S.Row
                key={index}
                data-testid='batch-table-row-test-id'
              >
                <S.Column>
                  <Typography>{formatDateReplaceHour(new Date(batch.expirationDate))}</Typography>
                </S.Column>
                <S.Column>
                  <Typography>{batch.quantity}</Typography>
                </S.Column>
                <S.Column>
                  <IconButton
                    style={{
                      color: 'red',
                      cursor: 'pointer',
                    }}
                    onClick={() => removeBatch(index)}
                    data-testid='button-batch-table-remove-test-id'
                  >
                    <DeleteOutline />
                  </IconButton>
                </S.Column>
              </S.Row>
            )) : (
              <S.Row>
                <S.Column style={{ width: '100%', justifyContent: 'center' }}>
                  <Typography>Nenhum lote adicionado</Typography>
                </S.Column>
              </S.Row>
            )}
          </S.ContainerRows>
        </S.Form>
        <S.ContainerButton>
          <S.CustomButton
            title='Salvar'
            bgColor='lightBlue3'
            hoverColor='darkBlueHover'
            letterColor='white'
            data-testid='button-save-batches-test-id'
            onClick={saveCountedBatches}
          />
        </S.ContainerButton>
      </S.Container>
      <ConfirmOrRejectModal
        show={showModalCountConfirmation}
        title='Confirmação de contagem'
        dataTestId='modal-count-confirmation-test-id'
        customizableDescription={(
          <S.ConfirmModalContainer>
            <S.ConfirmModalText>
              Ao confirmar a ação, você afirma que a contagem total (lote a lote) do produto <strong>{productDescription}</strong> foi realizada.
            </S.ConfirmModalText>
            <S.ConfirmModalConfirmationText>
              Deseja prosseguir?
            </S.ConfirmModalConfirmationText>
          </S.ConfirmModalContainer>
        )}
        onClose={() => setShowModalCountConfirmation(false)}
        onReject={() => setShowModalCountConfirmation(false)}
        onConfirm={saveCountedBatches}
      />
    </>
  )
}

export default FormCountBatches
